import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';

import type { AgentAddressMessage, AgentContactMessage, AgentRequestMessage } from '../../../types/AgentMessage';
import { Button } from '../../cdl/Button/Button';
import { DrawerContentLayout } from '../../cdl/Drawer/DrawerContentLayout';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { Text } from '../../cdl/Text/Text';
import { FormikTextArea } from '../../cdl/TextArea/FormikTextArea';
import { CreationComment } from '../../common/CreationComment/CreationComment';
import { FormikCustomerCompanySelect } from '../../common/form-elements/formik/FormikCustomerCompanySelect';
import { FormikDebug } from '../../common/form-elements/formik/FormikDebug';
import { translate } from '../../common/helpers/translate.helper';
import { FormikMultiPortSelect } from '../../common/MultiPortSelect/FormikMultiPortSelect';
import { Box } from '../../common/ui/Box';

export interface AgentFormValues extends AgentRequestMessage {
    address: AgentAddressMessage;
    contact: AgentContactMessage;
    note: string;
}

export interface AgentFormInitialValues extends Partial<Omit<AgentFormValues, 'customerId'>> {
    customerId?: string;
}

interface AgentFormProps {
    agent?: AgentFormInitialValues;
    comment?: string;
    onSubmit: (values: AgentFormValues) => void | Promise<void>;
    showCompanySelect?: boolean;
    primaryActionText: string;
}

const getInitialValues = (agent?: AgentFormInitialValues): AgentFormValues => ({
    id: agent?.id,
    name: agent?.name ?? '',
    contact: {
        name: agent?.contact?.name,
        phoneNumber: agent?.contact?.phoneNumber,
        emailAddress: agent?.contact?.emailAddress,
        faxNumber: agent?.contact?.faxNumber,
    },
    active: agent?.active,
    customerId: agent?.customerId ?? '',
    note: agent?.note ?? '',
    portIds: agent?.portIds ?? [],
    dateCreated: agent?.dateCreated,
    dateUpdated: agent?.dateUpdated,
    address: {
        companyName: agent?.address?.companyName,
        street: agent?.address?.street,
        streetNumber: agent?.address?.streetNumber,
        zipCode: agent?.address?.zipCode,
        city: agent?.address?.city,
        country: agent?.address?.country,
    },
    source: agent?.source,
});

export const AgentForm = ({
    agent,
    comment,
    onSubmit,
    showCompanySelect = false,
    primaryActionText,
}: AgentFormProps) => {
    const validate = (values: AgentFormValues) => {
        const errors: FormikErrors<AgentFormValues> = {};

        if (!values.name) {
            errors.name = translate('errormessage.empty');
        }

        if (!values.customerId) {
            errors.customerId = translate('errormessage.empty');
        }

        if (!values.portIds.length) {
            errors.portIds = translate('errormessage.empty');
        }

        return errors;
    };

    return (
        <Formik initialValues={getInitialValues(agent)} validate={validate} validateOnMount onSubmit={onSubmit}>
            {({ isSubmitting, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {primaryActionText}
                            </Button>
                        }
                    >
                        {comment ? <CreationComment text={comment} /> : null}

                        {showCompanySelect ? (
                            <Box>
                                <Text display="block" marginBottom={8}>
                                    {translate('agentForm.companyHint')}
                                </Text>
                                <FormikCustomerCompanySelect
                                    name="customerId"
                                    label={translate('agents.company')}
                                    markAsRequired
                                />
                            </Box>
                        ) : null}

                        <FormikInput label={translate('agentForm.agentName')} name="name" markAsRequired autoFocus />

                        <FormikInput label={translate('global.address.country')} name="address.country" />

                        <FormikMultiPortSelect name="portIds" label={translate('agentForm.ports')} markAsRequired />

                        <FormikInput label={translate('global.address.street')} name="address.street" />

                        <FormikInput label={translate('global.address.streetnumber')} name="address.streetNumber" />

                        <FormikInput label={translate('global.address.zipcode')} name="address.zipCode" />

                        <FormikInput label={translate('global.address.city')} name="address.city" />

                        <Text marginTop={24} as="h2" variant="subtitle1">
                            {translate('agentForm.contact.heading')}
                        </Text>

                        <FormikInput label={translate('agentForm.contact.name')} name="contact.name" />

                        <FormikInput label={translate('agentForm.contact.emailAddress')} name="contact.emailAddress" />

                        <FormikInput label={translate('agentForm.contact.phoneNumber')} name="contact.phoneNumber" />

                        <FormikInput label={translate('agentForm.contact.faxNumber')} name="contact.faxNumber" />

                        <Box paddingBottom={4}>
                            <FormikTextArea label={translate('agentForm.note')} name="note" />
                        </Box>

                        <FormikDebug />
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
